<template>
  <div class="app-shell">
  <!--
    첫 주소의 로그인 확인이 끝나기 전에는 포털 껍데기를 그리지 않는다.
    함우리몰은 새 탭이라 세션이 비어 보이는데, 그 사이에 예전 화면이 번쩍인다.
  -->
  <div
    v-if="!appReady"
    class="app-boot"
    role="status"
    aria-label="화면을 준비하고 있습니다"
  />

  <!-- 판매관리센터: 새 창에서 단독으로 뜨므로 사이트 헤더·푸터를 쓰지 않는다 -->
  <SellerLayout v-else-if="isSeller">
    <!-- Transition(out-in)이 SPA 이동 시 본문을 비운 채 멈추는 경우가 있어 셸에서는 바로 교체한다 -->
    <router-view />
  </SellerLayout>

  <!-- 관리자 화면도 자기 셸을 쓴다. 로그인 화면만 셸 없이 뜬다. -->
  <AdminLayout v-else-if="isAdmin">
    <router-view />
  </AdminLayout>

  <router-view v-else-if="isBare" />

  <div v-else ref="siteAppEl" class="site-app" :class="{ 'site-app--mall': isMall, 'site-app--gate': isGate, 'site-app--landing': isPortalHome }">
    <a v-if="isMall" href="#main-content" class="skip-link">본문으로 건너뛰기</a>
    <MallHeader v-if="isMall" />
    <PortalHeader v-else />

    <main id="main-content" class="site-main" :class="{ 'site-main--home': isHome }" tabindex="-1">
      <router-view v-if="isMall" v-slot="{ Component, route: viewRoute }">
        <!--
          몰은 out-in Transition 없이 바로 교체한다.
          Teleport/목록 패치와 겹치면 __vnode null 이 난다.
        -->
        <component
          :is="Component"
          :key="viewRoute.fullPath"
        />
      </router-view>
      <SectionFrame v-else-if="portalSection && !isPortalHome" :section="portalSection">
        <router-view v-slot="{ Component, route: viewRoute }">
          <!-- out-in Transition은 떠나는 화면이 끝난 뒤 다음 화면이 비어 멈추는 경우가 있다 -->
          <component
            :is="Component"
            :key="landViewKey(viewRoute)"
          />
        </router-view>
      </SectionFrame>
      <router-view v-else v-slot="{ Component, route: viewRoute }">
        <!--
          로그인 탭(?tab=seller)만 바꿀 때 fullPath key 로 리마운트되면
          판매업체 세션이 있을 때 입력도 없이 바로 입장되는 문제가 난다.
          약관처럼 같은 화면 안에서 주소만 바뀔 때도 out-in Transition이 본문을 비운다.
        -->
        <component
          :is="Component"
          :key="landViewKey(viewRoute)"
        />
      </router-view>
    </main>

    <PortalFooter v-if="!isGate" />

    <ScrollToTop />

    <MallToastHost v-if="isMall" />
    <MallBottomNav v-if="isMall" />
    <MobileBottomNav v-else-if="!isGate && !isPortalHome" />
  </div>

  <ConfirmDialog />
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter, type RouteLocationNormalizedLoaded } from 'vue-router'
import PortalHeader from '@/components/layout/PortalHeader.vue'
import PortalFooter from '@/components/layout/PortalFooter.vue'
import SectionFrame from '@/components/layout/SectionFrame.vue'
import ScrollToTop from '@/components/layout/ScrollToTop.vue'
import MobileBottomNav from '@/components/layout/MobileBottomNav.vue'
import MallHeader from '@/components/mall/MallHeader.vue'
import MallBottomNav from '@/components/mall/MallBottomNav.vue'
import MallToastHost from '@/components/mall/MallToastHost.vue'
import ConfirmDialog from '@/components/common/ConfirmDialog.vue'
import SellerLayout from '@/components/seller/SellerLayout.vue'
import AdminLayout from '@/components/admin/AdminLayout.vue'
import { useMallToast } from '@/composables/useMallToast'
import { useMemberAuth } from '@/composables/useMemberAuth'
import { saveRecentVisit } from '@/composables/usePortalStorage'
import { recordPortalVisit, shouldRecordPortalVisit } from '@/api/portalVisit'
import { applyDocumentTitle } from '@/utils/documentTitle'
import { illust } from '@/constants/illust'
import { landingGateOpen, landingTransit } from '@/composables/useOrgType'
import { preloadImages } from '@/utils/preloadImage'
import { glueKoreanTree } from '@/utils/glueKoreanLine'
import { portalSectionForPath } from '@/constants/portalSections'

const route = useRoute()
const router = useRouter()
const appReady = ref(false)
const siteAppEl = ref<HTMLElement | null>(null)
const LAND_ROUTE_NAMES = new Set(['landing', 'wage', 'facility', 'visiting-care'])
const isLandHome = computed(() => LAND_ROUTE_NAMES.has(String(route.name ?? '')))
const isHome = computed(() => isLandHome.value)
const LANDING_SHELL_NAMES = new Set(['dashboard', 'terms', 'login', 'info-plaza', 'info-detail', 'info-center', 'info-story'])
const isPortalHome = computed(() => LANDING_SHELL_NAMES.has(String(route.name ?? '')))
const isGate = computed(() => isLandHome.value && (landingGateOpen.value || landingTransit.value))
const portalSection = computed(() => portalSectionForPath(route.path))

function landViewKey(viewRoute: RouteLocationNormalizedLoaded) {
  const name = String(viewRoute.name ?? '')
  if (name === 'info-center' || name === 'info-detail') return '/info'
  if (name === 'login' || name === 'info-plaza') return viewRoute.path
  if (LAND_ROUTE_NAMES.has(name)) return 'land-home'
  return viewRoute.fullPath
}

const layoutOf = (name: string) =>
  route.matched.some((record) => record.meta.layout === name) || route.meta.layout === name
/** 함우리몰 라우트는 본 사이트 헤더·푸터를 몰 전용으로 통째로 교체한다. */
const isMall = computed(() => layoutOf('mall'))
/** 판매관리센터는 관리 화면이라 레이아웃 자체가 다르다. */
const isSeller = computed(() => layoutOf('seller'))
/** 관리자 화면 */
const isAdmin = computed(() => layoutOf('admin'))
/** 관리자 로그인처럼 셸 없이 혼자 뜨는 화면 */
const isBare = computed(() => layoutOf('bare'))

const { initMemberAuth } = useMemberAuth()
const { showError } = useMallToast()

function onOffline() {
  showError('네트워크 연결이 끊겼습니다. 연결 후 다시 시도해 주세요.')
}

let glueFrame = 0
let glueObserver: MutationObserver | null = null
function scheduleGlue() {
  if (isMall.value || !siteAppEl.value) return
  cancelAnimationFrame(glueFrame)
  glueFrame = requestAnimationFrame(() => {
    if (!isMall.value && siteAppEl.value) glueKoreanTree(siteAppEl.value)
  })
}

onMounted(async () => {
  window.addEventListener('offline', onOffline)
  /* 라우터 가드가 세션을 끝낼 때까지 헤더가 먼저 빈 계정으로 판단하지 않게 한다. */
  await router.isReady()
  appReady.value = true
  await nextTick()
  initMemberAuth()
  if (!isMall.value && !isSeller.value && !isAdmin.value) {
    preloadImages([illust.wage, illust.accounting])
  }
  scheduleGlue()
  if (siteAppEl.value) {
    glueObserver = new MutationObserver(() => scheduleGlue())
    glueObserver.observe(siteAppEl.value, { childList: true, subtree: true, characterData: true })
  }
})

watch(() => route.fullPath, () => {
  void nextTick(() => scheduleGlue())
})

onUnmounted(() => {
  glueObserver?.disconnect()
  cancelAnimationFrame(glueFrame)
  window.removeEventListener('offline', onOffline)
})

router.afterEach((to) => {
  applyDocumentTitle(to)
  const label = typeof to.meta.visitLabel === 'string' ? to.meta.visitLabel : String(to.name ?? to.path)
  saveRecentVisit(label, to.fullPath)

  const layout = String(to.meta.layout || to.matched.find((record) => record.meta.layout)?.meta.layout || '')
  if (shouldRecordPortalVisit(layout)) {
    void recordPortalVisit(to.query as Record<string, unknown>)
  }
})

/* 첫 진입·새로고침 시에도 탭 이름을 맞춘다 */
applyDocumentTitle(route)
</script>

<style lang="scss" src="./assets/styles/App.scss"></style>
